<template lang="pug">
.list-exceptions-ovg-container
  //- Header: Breadcrumb, título con flecha y buscador
  .container-fluid.px-4.py-3
    .breadcrumb-row.mb-2
      span.text-muted Inicio / Excepciones / 
      span.font-weight-bold Excepciones OVG

    .d-flex.flex-wrap.justify-content-between.align-items-center.mb-3
      .d-flex.align-items-center
        b-icon.back-arrow(
          icon="arrow-left-square-fill"
          @click="$router.go(-1)"
        )
        h3.mb-0.ml-2.font-weight-bold Excepciones OV Garantías
      
      .search-box.position-relative
        b-icon.search-icon(icon="search")
        b-form-input.search-input(
          v-model="searchTerm"
          placeholder="Buscar por OV / RMA / N° de Serie"
          @keydown.enter="handleSearch"
        )

    //- Barra de pestañas / Tabs
    .tabs-wrapper.mb-4
      .tabs-bar.d-flex.align-items-center
        button.tab-btn(
          v-for="(tab, idx) in tabs"
          :key="idx"
          :class="{ active: tabSelected === idx }"
          @click="selectTab(idx)"
        )
          | {{ tab.label }} ({{ tab.count }})

    //- Contenedor de tabla o estado vacío
    .content-card
      .text-center.py-5(v-if="loading")
        b-spinner(variant="warning" label="Cargando...")
        p.mt-3.text-muted Cargando excepciones...

      //- Estado Vacío (Mockup Figma SILL-1578)
      .empty-state.text-center.py-5(v-else-if="exceptions.length === 0")
        img.empty-illustration(
          src="@/assets/no_data_ov.jpg"
          alt="Sin excepciones"
        )
        h4.empty-title.mt-3.font-weight-bold ¡Excelente!
        p.empty-subtitle.text-muted
          | No tienes Excepciones de Oportunidad de Venta Garantías por aprobar

      //- Tabla de Excepciones
      div(v-else)
        .table-responsive
          table.table.table-hover.table-striped.ovg-exceptions-table.mb-0
            thead
              tr
                th O.V.G.
                th Fecha
                th Tipo
                th Estado
                th Solicitado por
                th N° de Serie
                th.text-right Monto de la rebaja
                th.text-center Acciones
            tbody
              tr(v-for="item in exceptions" :key="item.id")
                td.font-weight-bold.text-primary.cursor-pointer(@click="openDetail(item.id)")
                  | {{ item.ovg_id }}
                td {{ formatDate(item.fecha_creacion) }}
                td {{ item.tipo_display || item.tipo }}
                td
                  span.status-badge(:class="statusClass(item.estado)")
                    | {{ formatStatus(item.estado) }}
                td {{ item.solicitado_por_nombre || '-' }}
                td
                  span(v-if="item.cantidad_series > 1")
                    b-badge(variant="secondary") {{ item.cantidad_series }} series
                  span(v-else)
                    | {{ item.numero_serie || '-' }}
                td.text-right.font-weight-bold
                  | {{ formatCurrency(item.monto_rebaja) }}
                td.text-center
                  b-dropdown(
                    size="sm"
                    variant="warning"
                    right
                    no-caret
                    toggle-class="btn-acciones-dropdown"
                  )
                    template(#button-content)
                      b-icon(icon="three-dots" class="mr-1")
                      | Acciones
                    b-dropdown-item(@click="openDetail(item.id)")
                      b-icon.mr-2(icon="eye")
                      | Ver Detalle
                    b-dropdown-item(
                      v-if="item.estado === 'PENDIENTE'"
                      @click="promptQuickAction(item, 'aprobar')"
                    )
                      b-icon.mr-2(icon="check-circle" variant="success")
                      | Aprobar
                    b-dropdown-item(
                      v-if="item.estado === 'PENDIENTE'"
                      @click="promptQuickAction(item, 'rechazar')"
                    )
                      b-icon.mr-2(icon="x-circle" variant="danger")
                      | Rechazar
                    b-dropdown-item(disabled link-class="text-muted")
                      b-icon.mr-2(icon="trash")
                      | Eliminar

        //- Paginación
        .d-flex.justify-content-end.p-3(v-if="totalRows > 0")
          b-pagination(
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="20"
            align="right"
            prev-text="Ant."
            next-text="Sig."
            class="ovg-pagination mb-0"
            @change="handlePageChange"
          )

  //- Modal de Detalle
  ExceptionDetailModalOVG(
    v-if="showDetailModal"
    :visible="showDetailModal"
    :exception-id="selectedExceptionId"
    @close="closeDetail"
    @action-completed="onActionCompleted"
  )

  //- Modal de confirmación rápida (desde menú acciones)
  b-modal(
    id="quick-action-modal"
    v-model="showQuickModal"
    :title="quickModalTitle"
    centered
    :ok-title="quickModalOkTitle"
    :ok-variant="quickActionType === 'aprobar' ? 'success' : 'danger'"
    cancel-title="Cancelar"
    cancel-variant="light"
    @ok="confirmQuickAction"
  )
    p(v-if="selectedItem")
      | ¿Estás seguro de que deseas 
      strong {{ quickActionType === 'aprobar' ? 'aprobar' : 'rechazar' }}
      |  la excepción para la OVG 
      strong {{ selectedItem.ovg_id }}
      | ?
    b-form-group(label="Comentario (opcional)")
      b-form-textarea(
        v-model="quickComment"
        placeholder="Deja un comentario de resolución..."
        rows="2"
      )
</template>

<script>
import { mapActions } from 'vuex'
import moment from 'moment'
import { wsClient } from '../../utils/websocket'
import ExceptionDetailModalOVG from './ExceptionDetailModalOVG.vue'

export default {
  name: 'ListExceptionsOVG',
  components: {
    ExceptionDetailModalOVG,
  },
  data() {
    return {
      searchTerm: '',
      loading: false,
      exceptions: [],
      totalRows: 0,
      currentPage: 1,
      tabSelected: 0,
      tabs: [
        {
          label: 'Pendientes',
          key: 'pendientes',
          value: 'PENDIENTE',
          count: 0,
        },
        { label: 'Aprobadas', key: 'aprobadas', value: 'APROBADO', count: 0 },
        {
          label: 'Rechazadas',
          key: 'rechazadas',
          value: 'RECHAZADO',
          count: 0,
        },
        { label: 'Todas', key: 'total', value: 'TODAS', count: 0 },
      ],
      showDetailModal: false,
      selectedExceptionId: null,
      // Confirmación rápida
      showQuickModal: false,
      selectedItem: null,
      quickActionType: 'aprobar',
      quickComment: '',
    }
  },
  computed: {
    quickModalTitle() {
      return this.quickActionType === 'aprobar'
        ? 'Aprobar Excepción OVG'
        : 'Rechazar Excepción OVG'
    },
    quickModalOkTitle() {
      return this.quickActionType === 'aprobar' ? 'Aprobar' : 'Rechazar'
    },
  },
  async created() {
    await this.fetchCounts()
    await this.fetchExceptions(this.tabs[this.tabSelected].value, 1)

    // Escuchar websocket para recargas automáticas
    this.unsubscribeWs = wsClient.on('excepcion:updated', () => {
      this.fetchCounts()
      this.fetchExceptions(this.tabs[this.tabSelected].value, this.currentPage)
    })
  },
  beforeDestroy() {
    if (this.unsubscribeWs) {
      this.unsubscribeWs()
    }
  },
  methods: {
    ...mapActions('OVGCenterStore', [
      'getExcepcionesOVG',
      'getExcepcionesOVGConteo',
      'gestionarExcepcionOVG',
    ]),
    async fetchCounts() {
      try {
        const counts = await this.getExcepcionesOVGConteo()
        this.tabs[0].count = counts.pendientes || 0
        this.tabs[1].count = counts.aprobadas || 0
        this.tabs[2].count = counts.rechazadas || 0
        this.tabs[3].count = counts.total || 0
      } catch (err) {
        console.error('Error fetching exception counts:', err)
      }
    },
    async fetchExceptions(estado, page = 1) {
      this.loading = true
      try {
        const res = await this.getExcepcionesOVG({
          estado,
          search: this.searchTerm,
          page,
        })
        this.exceptions = res.results || []
        this.totalRows = res.count || 0
        this.currentPage = page
      } catch (err) {
        console.error('Error fetching exceptions list:', err)
        this.exceptions = []
        this.totalRows = 0
      } finally {
        this.loading = false
      }
    },
    selectTab(idx) {
      this.tabSelected = idx
      this.currentPage = 1
      this.fetchExceptions(this.tabs[idx].value, 1)
    },
    handleSearch() {
      this.currentPage = 1
      this.fetchExceptions(this.tabs[this.tabSelected].value, 1)
    },
    handlePageChange(page) {
      this.fetchExceptions(this.tabs[this.tabSelected].value, page)
    },
    openDetail(id) {
      this.selectedExceptionId = id
      this.showDetailModal = true
    },
    closeDetail() {
      this.showDetailModal = false
      this.selectedExceptionId = null
    },
    onActionCompleted() {
      this.fetchCounts()
      this.fetchExceptions(this.tabs[this.tabSelected].value, this.currentPage)
    },
    promptQuickAction(item, action) {
      this.selectedItem = item
      this.quickActionType = action
      this.quickComment = ''
      this.showQuickModal = true
    },
    async confirmQuickAction() {
      if (!this.selectedItem) return
      try {
        await this.gestionarExcepcionOVG({
          id: this.selectedItem.id,
          accion: this.quickActionType,
          comentario: this.quickComment,
        })
        this.$bvToast.toast(
          this.quickActionType === 'aprobar'
            ? 'Excepción aprobada exitosamente'
            : 'Excepción rechazada',
          {
            title: 'Gestión de Excepción',
            variant: this.quickActionType === 'aprobar' ? 'success' : 'danger',
            solid: true,
            autoHideDelay: 3000,
          }
        )
        this.fetchCounts()
        this.fetchExceptions(
          this.tabs[this.tabSelected].value,
          this.currentPage
        )
      } catch (err) {
        console.error('Error in quick action:', err)
        const msg =
          err?.response?.data?.error ||
          err?.response?.data?.detail ||
          'No se pudo procesar la solicitud'
        this.$bvToast.toast(msg, {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 4000,
        })
      } finally {
        this.showQuickModal = false
        this.selectedItem = null
      }
    },
    formatDate(d) {
      if (!d) return '-'
      return moment(d).format('DD/MM/YYYY')
    },
    formatCurrency(val) {
      if (val === null || val === undefined) return '$0'
      const num = Math.round(Number(val))
      return `$${num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`
    },
    formatStatus(st) {
      if (st === 'PENDIENTE') return 'Pendiente'
      if (st === 'APROBADO') return 'Aprobado'
      if (st === 'RECHAZADO') return 'Rechazado'
      return st || '-'
    },
    statusClass(st) {
      if (st === 'PENDIENTE') return 'status-badge-pending'
      if (st === 'APROBADO') return 'status-badge-approved'
      if (st === 'RECHAZADO') return 'status-badge-rejected'
      return ''
    },
  },
}
</script>

<style lang="scss" scoped>
.list-exceptions-ovg-container {
  min-height: calc(100vh - 60px);
  background-color: #f8f9fa;
}

.breadcrumb-row {
  font-size: 0.85rem;
}

.back-arrow {
  font-size: 1.8rem;
  color: #fece2f;
  cursor: pointer;
  transition: transform 0.15s ease-in-out;
  &:hover {
    transform: scale(1.08);
  }
}

.search-box {
  width: 100%;
  max-width: 340px;

  .search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #6c757d;
    z-index: 4;
  }
  .search-input {
    padding-left: 36px;
    border-radius: 20px;
    border: 1px solid #ced4da;
    height: 38px;
    font-size: 0.9rem;
  }
}

.tabs-wrapper {
  background-color: #f1f3f5;
  border-radius: 24px;
  padding: 4px;
  display: inline-block;
  max-width: 100%;
  overflow-x: auto;
}

.tabs-bar {
  display: flex;
  gap: 4px;
}

.tab-btn {
  background: transparent;
  border: none;
  border-radius: 20px;
  padding: 8px 20px;
  font-size: 0.9rem;
  font-weight: 500;
  color: #495057;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  white-space: nowrap;

  &:hover {
    color: #000;
  }

  &.active {
    background-color: #fece2f;
    color: #000;
    font-weight: 700;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  }
}

.content-card {
  background: #ffffff;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 1px solid #e9ecef;
  overflow: hidden;
}

.empty-state {
  padding: 60px 20px;
  .empty-illustration {
    max-width: 320px;
    width: 60%;
    height: auto;
    margin: 0 auto;
    display: block;
  }
  .empty-title {
    color: #212529;
    font-size: 1.4rem;
  }
  .empty-subtitle {
    font-size: 0.95rem;
  }
}

.ovg-exceptions-table {
  thead th {
    background-color: #e9ecef;
    color: #495057;
    font-size: 0.85rem;
    font-weight: 600;
    border: none;
    vertical-align: middle;
    padding: 12px 14px;
  }
  tbody td {
    vertical-align: middle;
    font-size: 0.9rem;
    padding: 12px 14px;
  }
}

.status-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;

  &.status-badge-pending {
    background-color: #fff3cd;
    color: #856404;
  }
  &.status-badge-approved {
    background-color: #d4edda;
    color: #155724;
  }
  &.status-badge-rejected {
    background-color: #f8d7da;
    color: #721c24;
  }
}

::v-deep .btn-acciones-dropdown {
  background-color: #fece2f !important;
  border-color: #fece2f !important;
  color: #000 !important;
  font-weight: 600;
  border-radius: 16px;
  padding: 4px 12px;
  font-size: 0.82rem;
  box-shadow: none !important;

  &:hover,
  &:focus,
  &:active {
    background-color: darken(#fece2f, 8%) !important;
    border-color: darken(#fece2f, 8%) !important;
    color: #000 !important;
  }
}

.cursor-pointer {
  cursor: pointer;
}
</style>
